// FTEDN — Toast notification system (Dark Lab Theme)
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import '../../styles/shared-ui.css';

type ToastType = 'success' | 'error' | 'info' | 'warning' | 'xp';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  exiting?: boolean;
}

interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextType>({ addToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
  xp: '⚡',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 250);
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const duration = toast.duration ?? 4000;
    setToasts(prev => [...prev.slice(-4), { ...toast, id }]); // max 5 toasts
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id}
               className={`toast toast--${toast.type} ${toast.exiting ? 'toast-exit' : ''}`}
               style={{ position: 'relative' }}>
            <div className="toast-icon">{ICONS[toast.type]}</div>
            <div className="toast-body">
              <div className="toast-title">{toast.title}</div>
              {toast.message && <div className="toast-message">{toast.message}</div>}
            </div>
            <button className="toast-close" onClick={() => removeToast(toast.id)}>✕</button>
            {(toast.duration ?? 4000) > 0 && (
              <div className="toast-progress"
                   style={{ animationDuration: `${toast.duration ?? 4000}ms` }} />
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
